import React, { createContext, useContext, useEffect, useState } from "react";
import Loading from "../components/Loading";
import axiosInstance from "../utils/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import { clearCookie, setCookie } from "../utilities/cookies";
import { SocketProvider } from "./useSocket";
import { SocketProvider as SocketEmployeeProvider } from "./useEmployeeSocket";
import { hasIdentityCallback, processIdentityCallback } from "../Worksuite/auth/identityCallback";
import { clearIdentitySession, readStoredUser } from "../Worksuite/auth/authStorage";
import { verifyChatIdentitySession as verifyIdentitySession } from "../Worksuite/auth/identitySession";

const authorizeContext = createContext();

const AuthorizationProvider = ({ children }) => {
  const [content, setContent] = useState(<Loading message="Please wait, checking your session..." />);
  const [user, setUser] = useState({});
  const [currentOrganization, setCurrentOrganization] = useState(() => {
    try { return JSON.parse(localStorage.getItem("org") || "{}"); } catch { return {}; }
  });
  const navigate = useNavigate();
  const location = useLocation();

  const authorize = (loggedIn, cb) => {
    if (loggedIn) setContent(children);
    else setContent(<Loading message="Please wait, returning to Chat..." redirectTo="/login" />);
    if (typeof cb === "function") cb(setUser);
  };

  const signOut = () => {
    clearCookie("accessToken");
    clearIdentitySession();
    localStorage.removeItem("subscriptionId");
    localStorage.removeItem("org");
    setContent(<Loading message="Signing you out..." redirectTo="/login" />);
  };

  const checkOrganization = async () => {
    let selectedOrg = localStorage.getItem("org");
    if (!selectedOrg) {
      if (!localStorage.getItem("pendingInvitationToken")) navigate("/listOrganization");
      return;
    }
    try {
      selectedOrg = JSON.parse(selectedOrg);
      const requestOrg = { ...selectedOrg }; delete requestOrg.logo;
      const response = await axiosInstance.post(`/api/organization/check`, { organizationId: requestOrg._id, organization: requestOrg });
      if (!response.data?.success) throw new Error("organization_inactive");
      setCookie("orgToken", response.data.data);
      setCurrentOrganization(selectedOrg);
    } catch {
      localStorage.removeItem("org");
      if (!localStorage.getItem("pendingInvitationToken")) navigate("/listOrganization");
    }
  };

  const getOrganizations = async () => {
    try {
      const response = await axiosInstance.get(`/api/organization`);
      if (response.data?.success && response.data.data?.length === 0 && !localStorage.getItem("pendingInvitationToken")) {
        navigate("/createOrganization"); return;
      }
      await checkOrganization();
    } catch { await checkOrganization(); }
  };

  const createSession = async (refreshToken, identityUser) => {
    const response = await axiosInstance.post(`/api/auth/open/session`, {
      refreshToken,
      userId: identityUser._id,
      userType: "user",
      email: identityUser.email,
      username: identityUser.username,
      firstName: identityUser.firstName,
      lastName: identityUser.lastName,
      avatarImage: identityUser.profilePicture || identityUser.avatarImage,
    });
    if (!response.data?.success || !response.data?.token) throw new Error("chat_session_failed");
    setCookie("accessToken", response.data.token);
    await getOrganizations();
    setUser(identityUser);
    setContent(children);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const pathName = window.location.pathname;
        const params = new URLSearchParams(window.location.search);
        if (pathName.startsWith("/accept-agent-invite") && params.get("token")) localStorage.setItem("pendingInvitationToken", params.get("token"));
        if (pathName.startsWith("/organization/")) {
          const token = pathName.split("/")[2]; if (token) localStorage.setItem("pendingInvitationToken", token);
        }

        let identityUser = null;
        if (hasIdentityCallback()) {
          const result = await processIdentityCallback();
          identityUser = result?.user || null;
          if (result?.destination && result.destination !== location.pathname) navigate(result.destination, { replace: true });
        } else {
          identityUser = readStoredUser();
          if (!identityUser) { if (active) authorize(false); return; }
          const verification = await verifyIdentitySession();
          if (verification.status === "inactive") { if (active) authorize(false); return; }
          // On a temporary Accounts outage retain the previously verified local product state.
        }

        const refreshToken = localStorage.getItem("refreshToken") || identityUser?.refreshToken;
        if (!identityUser?._id || !refreshToken) { authorize(false); return; }
        await createSession(refreshToken, identityUser);
      } catch (error) {
        console.error("Chat identity bootstrap failed", error);
        clearIdentitySession();
        if (active) authorize(false);
      }
    })();
    return () => { active = false; };
    // Product auth bootstrap is intentionally one transaction per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <authorizeContext.Provider value={{ authorize, signOut, setUser, user, setContent, currentOrganization }}>
      <SocketEmployeeProvider><SocketProvider>{content}</SocketProvider></SocketEmployeeProvider>
    </authorizeContext.Provider>
  );
};

const useAuthorize = () => useContext(authorizeContext).authorize;
const useUser = () => useContext(authorizeContext)?.user;
const useOrganization = () => useContext(authorizeContext)?.currentOrganization;
const useSetUser = () => useContext(authorizeContext).setUser;
const useSetContent = () => useContext(authorizeContext).setContent;
const useEmployees = () => useContext(authorizeContext)?.employees;

export default AuthorizationProvider;
export { useAuthorize, useUser, useSetUser, useSetContent, useEmployees, useOrganization, authorizeContext };
